import { isContractViolationError } from './contract-violation';
import { extractErrorCode } from './error-handler';

/**
 * 데이터 가용성 응답 규약 — 클라 판정부 (TRAD-2273 / 2281 / 2151 / 2152).
 *
 * ## 서버가 무엇을 바꿨나
 * 수집형 API(ticker·김치프리미엄)가 **수집 실패를 `200 + []` 로 표현하던 것을 그만두고**
 * `503 + {"code":"E_005"}` 로 내려준다. 이전에는 "장애"와 "정상 0건"이 응답상 완전히 같아서
 * 클라의 에러 UI 가 **원리적으로 실행될 수 없는 죽은 코드**였다
 * (TRAD-2130: 수집기가 죽었는데 스케줄러는 421회 SUCCESS 로 기록됐다).
 *
 * ## 🔴 이 파일에 t() 를 두지 않는다
 * `error-localize.ts` 와 달리 여기는 **순수 판정만** 한다. 로케일에 의존하지 않으므로
 * `shared/api` 배럴로 내보낼 수 있고, mock 없이 단위 테스트된다.
 * 문구 매핑은 `error-localize.ts`(E_005/E_006 → i18n 키)가 담당한다.
 *
 * ## dev 실응답 (2026-08-04 실측, 문서가 아니라 실제 응답 기준)
 * ```
 * GET /api/v1/kimchi-premium/current?exchange=NOPE
 *   → 503  Retry-After: 30
 *     {"code":"E_005","message":"Data temporarily unavailable"}
 * ```
 * 에러 봉투는 **최상위 `{code,message}`** 다(`result` 안에 감싸지 않는다).
 */

/** 상류/수집 실패로 지금 데이터를 줄 수 없음. **재시도하면 될 수 있다.** */
export const DATA_UNAVAILABLE_CODE = 'E_005';

/** **계획된** 점검. 재시도를 유도하지 않는다 — 눌러봐야 같은 화면이다. */
export const MAINTENANCE_CODE = 'E_006';

/**
 * TRAD-2767 — 도미넌스 홀더가 비어 있음(`DominanceService.getDominance()`). E_005 와 다른
 * 코드지만 의미는 같다(일시적으로 데이터가 없음, 재시도하면 될 수 있음) — `unavailable`로
 * 함께 분류한다.
 *
 * 🔴 이 code가 유저 응답으로 나가는 경로는 `getDominance()`(홀더가 비면 던짐) **하나뿐이다.**
 * 실제 CoinGecko 호출 실패(`DominanceService.fetchFromApi()`)는 스케줄러 전용 메서드이고 그
 * 예외가 유저 응답으로 나가지 않는다 — 유저가 이 code를 받았다면 항상 "홀더가 비어
 * 있다"(=아직 준비 안 됨)는 뜻이지, 그 밖의 다른 실패 사유가 이 code로 섞여 오는 경우가
 * 없다(backend `DominanceService.java` 실측 확인, 2026-08-21).
 */
export const DOMINANCE_NOT_READY_CODE = 'GET_DOMINANCE_FAILED';

/**
 * 장애 화면이 취해야 할 태도.
 *
 * - `maintenance` — 점검. **재시도 버튼을 주지 않는다.**
 * - `contract-violation` — 서버가 200 으로 규격을 어긴 **영구** 상태. 재시도 버튼을 주지 않는다.
 * - `unavailable` — 일시적 가용성 문제. 재시도 버튼을 준다.
 * - `generic` — 그 밖의 실패(네트워크 끊김·5xx·타임아웃). 기존 문구 그대로 재시도.
 */
export type DataErrorKind = 'maintenance' | 'unavailable' | 'generic' | 'contract-violation';

/**
 * 서버 code 문자열 → 표시 태도. **순수 함수**.
 *
 * 🔴 여기서는 **절대 `contract-violation` 이 나오지 않는다.** 서버가 그 상태를 알리는 code 를
 * 주지 않기 때문이다(어겼다는 사실을 아는 쪽은 응답을 검증한 클라뿐이다). 그 판정은
 * `dataErrorKind` 가 **에러 타입**으로만 한다.
 *
 * 🔴 code 를 모를 때(`null`)는 반드시 `generic` 이다. 알 수 없는 실패를 "점검 중"이라고
 * 말하면 화면이 거짓말을 하는 것이고(TRAD-2143), 사용자는 멀쩡한 재시도 기회를 잃는다.
 */
export const dataErrorKindFromCode = (code: string | null | undefined): DataErrorKind => {
  if (code === MAINTENANCE_CODE) return 'maintenance';
  if (code === DATA_UNAVAILABLE_CODE) return 'unavailable';
  if (code === DOMINANCE_NOT_READY_CODE) return 'unavailable';
  return 'generic';
};

/**
 * 에러 객체 → 표시 태도.
 *
 * **계약 위반(TRAD-2365)을 먼저 가른다.** 응답 검증에서 던져진 에러는 axios 에러가 아니라
 * code 가 없으므로, 순서를 뒤집으면 `generic`("일시적인 문제…다시 시도")으로 떨어져
 * **영구 결함을 일시 장애로 위장**하게 된다 — 그게 2365 의 원 결함이다.
 */
export const dataErrorKind = (error: unknown): DataErrorKind => {
  if (isContractViolationError(error)) return 'contract-violation';
  return dataErrorKindFromCode(extractErrorCode(error));
};

/**
 * 재시도 버튼을 보여도 되는가. **눌러도 결과가 달라지지 않는 상태**에서만 false 다 —
 * 점검(E_006)과 계약 위반(TRAD-2365)이 그렇다. 계약 위반은 서버가 같은 200 을 다시 주므로
 * 재시도 버튼은 사용자를 무한 재시도로 유도할 뿐이다.
 *
 * 🔴 **양방향 주의**: "점검이면 숨긴다"만 검사하면 조건을 통째로 뒤집어
 * **정상 실패에서도 재시도를 숨기는** 구현이 초록으로 통과한다. 테스트는 반대 방향
 * (일반 실패·E_005 에서는 재시도가 **보인다**)도 함께 고정한다.
 */
export const isRetryable = (kind: DataErrorKind): boolean => kind !== 'maintenance' && kind !== 'contract-violation';
